/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * PII redaction for Google Tag Manager egress (SMA-2670).
 *
 * GTM/Google is a US processor. For a Dutch regulated (mortgage) product, any
 * event property that becomes user-identifiable is a cross-border PII concern.
 * `createGTMTracker` spreads arbitrary `...properties` into the pushed event,
 * so the tracker is the single chokepoint through which every GTM event flows
 * — we redact here rather than trying to audit every `track*` call site.
 *
 * Strategy (defence in depth, fail-safe):
 *   1. Key denylist — drop any property whose key looks identifying
 *      (email, phone, name, BSN, IBAN, address, DOB, etc.), matched
 *      case-insensitively as a substring so `customerEmail`, `user_bsn`,
 *      `phoneNumber` are all caught.
 *   2. Value scrubbing — for surviving string values, redact embedded
 *      email addresses, Dutch BSN-like 9-digit numbers and IBANs, so a PII
 *      value that slipped through a benign-looking key (e.g. `label`,
 *      `query`) is still masked.
 *
 * This is intentionally conservative: it never throws and always returns a
 * plain object safe to forward. It does NOT prove upstream consent/DPA
 * coverage — see the manual-audit note in the PR.
 */

const REDACTED = "[redacted]";

/**
 * Substrings that, if present in a property key (case-insensitive), mark the
 * whole value as identifying and cause it to be dropped.
 */
const DENYLISTED_KEY_SUBSTRINGS = [
  "email",
  "e-mail",
  "phone",
  "tel",
  "mobile",
  "firstname",
  "first_name",
  "lastname",
  "last_name",
  "fullname",
  "full_name",
  "surname",
  "birth",
  "dob",
  "bsn",
  "iban",
  "ssn",
  "passport",
  "postcode",
  "postalcode",
  "postal_code",
  "zipcode",
  "zip_code",
  "address",
  "street",
  "housenumber",
  "house_number",
  "password",
  "token",
  "authorization",
];

const EMAIL_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;
const IBAN_RE = /\b[A-Z]{2}\d{2}[A-Z0-9]{10,30}\b/g;
// Dutch BSN: standalone 9-digit number (avoid masking longer id strings).
const BSN_RE = /\b\d{9}\b/g;

function keyIsDenylisted(key: string): boolean {
  const normalized = key.toLowerCase();
  return DENYLISTED_KEY_SUBSTRINGS.some((needle) =>
    normalized.includes(needle),
  );
}

function scrubStringValue(value: string): string {
  return value
    .replace(EMAIL_RE, REDACTED)
    .replace(IBAN_RE, REDACTED)
    .replace(BSN_RE, REDACTED);
}

function scrubValue(value: unknown): unknown {
  if (typeof value === "string") {
    return scrubStringValue(value);
  }
  if (Array.isArray(value)) {
    return value.map(scrubValue);
  }
  if (value && typeof value === "object") {
    return redactPII(value as Record<string, unknown>);
  }
  return value;
}

/**
 * Returns a shallow-safe, deeply-redacted copy of a GTM event object.
 * Denylisted keys are removed entirely; surviving string values are scrubbed
 * for embedded PII. Nested objects/arrays are processed recursively.
 */
export function redactPII<T extends Record<string, any>>(event: T): T {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(event)) {
    if (keyIsDenylisted(key)) {
      continue;
    }
    result[key] = scrubValue(value);
  }

  return result as T;
}
